import { useTranslation } from '@exam-room/translations';
import { useMemo } from 'react';

import {
  AddOutlinedIcon,
  Header,
  IconButton,
  LanguageSwitchMenu,
  RemoveOutlinedIcon,
  Timer,
  Tooltip,
} from '../../../components';
import { useAuth, useExamCard, useExamEvent } from '../../../providers';
import { useFontSize } from '../contexts/FontSizeContext';

export default function ExamEventHeader() {
  const { t, i18n } = useTranslation();
  const { language, changeLanguage } = i18n;
  const { user } = useAuth();
  const { increaseFontSize, decreaseFontSize, fontScale } = useFontSize();
  const { closestExam, examDuration } = useExamEvent();
  const { examCard } = useExamCard();

  const examStartedAt = useMemo(
    () => (closestExam?.startedAt ? new Date(closestExam?.startedAt) : null),
    [closestExam?.startedAt],
  );

  const adjustedExamEndedAt = useMemo(
    () => (closestExam?.endedAt ? new Date(closestExam.endedAt) : null),
    [closestExam?.endedAt],
  );

  const displayedExamEndedAt = useMemo(
    () =>
      closestExam?.globalAdjustedEndDate
        ? new Date(
            closestExam.globalAdjustedEndDate.getTime() +
              (examDuration?.totalManualExtendedDurationInMs ?? 0),
          )
        : null,
    [closestExam?.globalAdjustedEndDate, examDuration?.totalManualExtendedDurationInMs],
  );

  return (
    <Header
      left={
        <div className='flex items-center gap-4'>
          <img src='/logo.svg' alt='logo' className='h-10' />
          <div className='text-text-primary text-body2 flex flex-col gap-0.5'>
            <div>
              {t('header.seatNumber')} {examCard.seatNumber ?? '-'}
            </div>
            <div className='line-clamp-1 font-semibold break-all'>
              {user?.displayName} {user?.idNumber}
            </div>
          </div>
        </div>
      }
      middle={
        <>
          {closestExam && examStartedAt && displayedExamEndedAt && adjustedExamEndedAt ? (
            <Timer
              startedAt={examStartedAt}
              adjustedEndedAt={adjustedExamEndedAt}
              displayedEndedAt={displayedExamEndedAt}
              totalManualExtendedDurationInMs={examDuration?.totalManualExtendedDurationInMs}
            />
          ) : null}
        </>
      }
      right={
        <div key='font-size-adjust' className='flex items-center gap-1'>
          <Tooltip
            content={t('header.fontSizeDecrease')}
            position='bl'
            className='z-50 font-semibold'
          >
            <IconButton onClick={decreaseFontSize} disabled={fontScale === -1}>
              <RemoveOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip content={t('header.fontSize')} position='bl' className='z-50 font-semibold'>
            <div className='text-body1 bg-grey-100 grid size-10 place-items-center rounded-sm px-3 py-2 font-semibold'>
              A
            </div>
          </Tooltip>
          <Tooltip
            content={t('header.fontSizeIncrease')}
            position='bl'
            className='z-50 font-semibold'
          >
            <IconButton onClick={increaseFontSize} disabled={fontScale === 1}>
              <AddOutlinedIcon />
            </IconButton>
          </Tooltip>
          <LanguageSwitchMenu
            key='language-switch-menu'
            currentLanguage={language.toUpperCase()}
            onSwitchToEnglish={() => {
              changeLanguage('en');
            }}
            onSwitchToThai={() => {
              changeLanguage('th');
            }}
          />
        </div>
      }
    />
  );
}
